import React from 'react';
import { Button } from '../components/Button';
import { VideoEmbed } from '../components/VideoEmbed';
import { Check, Phone, Mail, MapPin } from 'lucide-react';
export function TrainingPage() {
  const benefits = ['Personalized training plans adapted to your fitness level', 'Technical kickboxing instruction with focus on proper form', 'Strength and conditioning tailored for martial arts', 'Nutritional guidance and lifestyle coaching', 'Flexible scheduling in central Warsaw', 'English-speaking coaching environment'];
  const trainingSteps = [{
    title: 'Foundation & Safety',
    description: "Learn stance, guard, balance, and basic defense so you don't build bad habits or get injured.",
    benefit: 'Correct form from day one'
  }, {
    title: 'Basic Striking Mechanics',
    description: 'Jab, cross, kick, knees, and simple combinations. Power comes from technique, not strength.',
    benefit: 'Real-time corrections and drills tailored to you'
  }, {
    title: 'Defense & Movement',
    description: 'Blocking, slipping, checking kicks, footwork, and distance control.',
    benefit: 'Situational drills at your pace'
  }, {
    title: 'Conditioning for Fighting',
    description: 'Cardio, core, and explosiveness specific to combat sports.',
    benefit: 'Conditioning built into sessions, not random workouts'
  }, {
    title: 'Controlled Pressure & Sparring Prep',
    description: 'Pad work, timing, and decision-making under stress.',
    benefit: 'Gradual exposure without being thrown into chaos'
  }, {
    title: 'Consistency & Progress Tracking',
    description: 'Structured progression, clear goals, and measurable improvement.',
    benefit: 'Plan adjusted weekly based on your progress'
  }];
  const whatToExpect = ['Initial assessment of your fitness level and goals', 'Customized training program designed for you', 'Proper technique instruction to prevent injury', 'Progressive training that builds real strength', 'Ongoing support and program adjustments', "A training partner who's invested in your success"];
  return <main className="flex-grow bg-black" data-id="element-293">
      {/* Hero */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center" data-id="element-294">
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6" data-id="element-295">
          1-on-1 Kickboxing & Fitness Coaching
        </h1>
        <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8" data-id="element-296">
          Professional personal training in Warsaw. Build real skills,
          confidence, and functional strength in a supportive environment.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-297">
          <Button variant="primary" size="lg" href="tel:+48536052998" data-id="element-298">
            <Phone className="w-5 h-5 mr-2" data-id="element-299" />
            Book Free Consultation
          </Button>
          <Button variant="outline" size="lg" href="mailto:contact@szulcaleks.com" data-id="element-300">
            <Mail className="w-5 h-5 mr-2" data-id="element-301" />
            Email Me
          </Button>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-302">
        <VideoEmbed videoId="dQw4w9WgXcQ" title="Training Philosophy" thumbnailUrl="https://images.unsplash.com/photo-1555597673-b21d5c935865?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80" data-id="element-303" />
        <p className="text-center text-sm text-gray-400 mt-4" data-id="element-304">
          Watch a short introduction to my training approach
        </p>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-black to-gray-900 border-y border-gray-800" data-id="element-305">
        <div className="max-w-7xl mx-auto" data-id="element-306">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-307">
            <div data-id="element-308">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-309">
                Why Train With Me?
              </h2>
              <p className="text-gray-300 mb-8 leading-relaxed" data-id="element-310">
                Unlike typical gym classes where you're just another number, my
                1-on-1 sessions are completely focused on your progress. We work
                on technique, strategy, and specific conditioning that
                translates to real-world capability.
              </p>
              <ul className="space-y-4" data-id="element-311">
                {benefits.map((benefit, index) => <li key={index} className="flex items-start" data-id="element-312">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4FF00] flex items-center justify-center mt-0.5 mr-3" data-id="element-313">
                      <Check className="w-4 h-4 text-black" strokeWidth={3} data-id="element-314" />
                    </div>
                    <span className="text-gray-300" data-id="element-315">{benefit}</span>
                  </li>)}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-800 h-full min-h-[400px]" data-id="element-316">
              <img src="https://images.unsplash.com/photo-1549719386-74dfc4417351?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Training Session" className="w-full h-full object-cover" data-id="element-317" />
            </div>
          </div>
        </div>
      </section>

      {/* 6-Step Training Plan */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-318">
        <div className="text-center mb-12" data-id="element-319">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-320">
            From Zero to Competent in Combat Sports
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto" data-id="element-321">
            My structured 6-step approach takes you from complete beginner to
            confident fighter, with personalized coaching at every stage.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-322">
          {trainingSteps.map((step, idx) => <div key={idx} className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 hover:border-[#D4FF00]/30 transition-colors" data-id="element-323">
              <div className="flex items-start gap-4" data-id="element-324">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#D4FF00] flex items-center justify-center text-black font-bold text-xl" data-id="element-325">
                  {idx + 1}
                </div>
                <div className="flex-1" data-id="element-326">
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-327">
                    {step.title}
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-328">
                    {step.description}
                  </p>
                  <div className="bg-black/50 border border-gray-700 rounded-lg p-3" data-id="element-329">
                    <p className="text-sm text-[#D4FF00] font-medium" data-id="element-330">
                      ✓ Personal training benefit: {step.benefit}
                    </p>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </section>

      {/* What To Expect */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto bg-gradient-to-b from-gray-900 to-black border-y border-gray-800" data-id="element-331">
        <h2 className="text-3xl font-bold text-white text-center mb-12" data-id="element-332">
          What To Expect
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-333">
          {whatToExpect.map((item, idx) => <div key={idx} className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 hover:border-[#D4FF00]/30 transition-colors" data-id="element-334">
              <div className="flex items-start gap-3" data-id="element-335">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#D4FF00]/10 flex items-center justify-center text-[#D4FF00] font-bold" data-id="element-336">
                  {idx + 1}
                </div>
                <p className="text-gray-300 leading-relaxed" data-id="element-337">{item}</p>
              </div>
            </div>)}
        </div>
      </section>

      {/* Location & Contact */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-black to-gray-900 border-t border-gray-800" data-id="element-338">
        <div className="max-w-4xl mx-auto text-center space-y-8" data-id="element-339">
          <h2 className="text-3xl font-bold text-white" data-id="element-340">Training Location</h2>
          <div className="flex items-center justify-center gap-2 text-gray-300 text-lg" data-id="element-341">
            <MapPin className="w-5 h-5 text-[#D4FF00]" data-id="element-342" />
            <span data-id="element-343">Central Warsaw, Poland</span>
          </div>

          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8 space-y-6" data-id="element-344">
            <h3 className="text-2xl font-bold text-white" data-id="element-345">Get Started Today</h3>
            <p className="text-gray-300 max-w-2xl mx-auto" data-id="element-346">
              Book a free consultation call to discuss your goals, training
              experience, and schedule. We'll determine if we're a good fit and
              I'll answer any questions you have.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4" data-id="element-347">
              <Button variant="primary" size="lg" href="tel:+48536052998" data-id="element-348">
                <Phone className="w-5 h-5 mr-2" data-id="element-349" />
                +48 536 052 998
              </Button>
              <Button variant="outline" size="lg" href="mailto:contact@szulcaleks.com" data-id="element-350">
                <Mail className="w-5 h-5 mr-2" data-id="element-351" />
                contact@szulcaleks.com
              </Button>
            </div>

            <p className="text-sm text-gray-400" data-id="element-352">
              📞 Free consultation call • 💪 Discounted first training session
            </p>
          </div>
        </div>
      </section>
    </main>;
}